import moment from "moment";
import { message } from "antd";
import { Diagnostico } from "shared/interfaces/Diagnostico";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import {
  fetchUserCredits,
  fetchUserDiagnostics,
} from "shared/service/StrmService";
import { useSelector } from "react-redux";
import { TIPO_DIAGNOSTICO_UTILIZADO } from "shared/constants/StorageConsts";
import { TipoDiagnostico } from "shared/enums/TipoDiagnostico";
import ApresentacaoDiagnostico from "./ApresentacaoDiagnostico";
import CardAssessment from "./CardAssessment";
import { ReactComponent as Clock } from "shared/assets/images/clock-eight.svg";
import { ReactComponent as PendingClock } from "shared/assets/images/pending-clock-.svg";
import "./Diagnosticos.scss";
import "./DiagnosticosInit.scss";
import { AdvanceWrapper } from "shared/components/smart-wrapper/SmartWrapper";
import { motion } from "motion/react";
import { ArtistFeatureCard } from "shared/components/artist-feature-card/ArtistFeatureCard";
import LogoAssessment from "shared/assets/images/icon-assessment-side-menu.svg";
import ImageVideoAssessment from "shared/assets/images/image-video-assessment.webp";
import ImageVidePlayButtonAssessment from "shared/assets/images/image-video-play-button-assessment.png";
import { Accordion, PopUpGlobal } from "shared/components";
import YouTubePlayer from "shared/components/youtube-player/YouTubePlayer";
import getAccordionItems from "./accordionItems";
import Simple from "shared/components/loader/Simple";
const { REACT_APP_DIAGNOSE_EXAMPLE } = process.env;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const Diagnosticos: React.FC<RouteComponentProps> = (props) => {
  const { history, location } = props;
  const [diagnosticos, setDiagnosticos] = useState<Diagnostico[]>([]);
  const { loggedUser, loading } = useSelector((state: any) => state.users);
  const [creditos, setCreditos] = useState<number>(0);
  const [convites, setConvites] = useState<number>(0);
  const [dataExpiracao, setDataExpiracao] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [videoPopUpVisible, setVideoPopUpVisible] = useState(false);
  const { t } = useTranslation();

  const formatDate = (date: string = "") => moment(date).format("DD/MM/YYYY");

  const onClickComprar = () =>
    history.push("/checkout-diagnostico", { buy: true });

  const onClickUsarCredito = () => {
    localStorage.setItem(TIPO_DIAGNOSTICO_UTILIZADO, TipoDiagnostico.User);
    history.push("/novo-diagnostico");
  };

  const onClickUserConvite = () => {
    localStorage.setItem(TIPO_DIAGNOSTICO_UTILIZADO, TipoDiagnostico.Team);
    history.push("/novo-diagnostico");
  };

  useEffect(() => {
    async function getUserDiagnostics() {
      const response: any = await fetchUserDiagnostics();
      setDiagnosticos(response);
    }

    async function getUserCredits() {
      const response: any = await fetchUserCredits();
      setCreditos(response.remainingCredits);
      setConvites(response.remainingInviteCredits);
      if (response.daysUntilExpiresInvites)
        setDataExpiracao(
          moment(response.daysUntilExpiresInvites).format("DD/MM/YYYY"),
        );
    }

    Promise.all([getUserDiagnostics(), getUserCredits()]).finally(() =>
      setLoadingData(false),
    );
    const state: any = location.state;
    if (state?.error) {
      message.error(t("pages.Diagnosticos.erroCompra"));
    }
    if (state?.buySuccess)
      message.success(t("pages.Diagnosticos.sucessoCompra"));
  }, [location, t]);

  if (loading || loadingData) return <Simple />;

  if (creditos === 0 && convites === 0 && diagnosticos?.length === 0)
    return (
      <div className="diagnosticos">
        <ApresentacaoDiagnostico
          showPageHeader
          title={t("pages.Diagnosticos.PreviaDiagnostico.acelereSuaCarreira")}
        />
      </div>
    );

  return (
    <AdvanceWrapper>
      <motion.aside
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
        viewport={{ once: true }}
      >
        <ArtistFeatureCard
          isPageInfo={true}
          title={t("pages.Diagnosticos.tituloCard")}
          subtitle={t("pages.Diagnosticos.subtituloCard")}
          image={LogoAssessment}
        />
      </motion.aside>
      <div>
        <div className="content">
          <motion.h2
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
            viewport={{ once: true }}
          >
            {t("pages.Diagnosticos.tituloStrm")}
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
            viewport={{ once: true }}
            className="assessment-description"
          >
            <Trans i18nKey={t("pages.Diagnosticos.explicacao")} />
          </motion.p>
          {convites > 0 && dataExpiracao && (
            <motion.div
              className="remaining-time"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
              viewport={{ once: true }}
            >
              <Clock />
              <span>
                <Trans
                  i18nKey={t("pages.Diagnosticos.tempoRestante", {
                    dias: dataExpiracao,
                  })}
                />
              </span>
            </motion.div>
          )}
          <motion.div
            className="credits-buttons"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
            viewport={{ once: true }}
          >
            {creditos === 0 && (
              <button className="botao-roxo" onClick={onClickComprar}>
                {t("pages.Diagnosticos.comprar")}
              </button>
            )}
            {creditos > 0 && (
              <button className="botao-roxo" onClick={onClickUsarCredito}>
                {t("pages.Diagnosticos.usarCredito")}
              </button>
            )}
            {convites > 0 && (
              <button className="botao-borda-roxa" onClick={onClickUserConvite}>
                {t("pages.Diagnosticos.usarConvite")}
              </button>
            )}
            <a
              className="botao-exemplo"
              href={REACT_APP_DIAGNOSE_EXAMPLE}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("pages.Diagnosticos.exemplo")}
            </a>
          </motion.div>
          <motion.div
            className="box-invite-credit"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
            viewport={{ once: true }}
          >
            <span>
              <Trans i18nKey={t("pages.Diagnosticos.creditos", { creditos })} />
            </span>
            <div className="divisor" />
            <span>
              <Trans
                i18nKey="pages.Diagnosticos.convites"
                values={{ convites }}
                components={{
                  strong: <strong style={{ color: "hsla(0, 0%, 95%, 0.5)" }} />,
                }}
              />
            </span>
          </motion.div>
          {loggedUser?.paymentPending && (
            <motion.div
              className="pending-investor"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
              viewport={{ once: true }}
            >
              <PendingClock />
              <span>{t("pages.Diagnosticos.pagamentoPendente")}</span>
            </motion.div>
          )}
          {diagnosticos?.length > 0 && (
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="my-assessments">
                <img src={LogoAssessment} alt="Logo Assessment" />
                <span> {t("pages.Diagnosticos.myAssessments")}</span>
              </div>
              <div className="list-assessments">
                {diagnosticos.map((diagnostico) => (
                  <div key={diagnostico?.url}>
                    {diagnostico?.pendingInvestorAnswer && (
                      <div className="pending-investor">
                        <Clock />
                        <span>
                          {t("pages.Diagnosticos.pendenteInvestidor")}
                        </span>
                      </div>
                    )}

                    <CardAssessment
                      diagnostico={diagnostico}
                      formatDate={formatDate}
                      OnAccess={() =>
                        history.push("/diagnostico", diagnostico?.url)
                      }
                    />
                  </div>
                ))}
                <motion.div
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
                  viewport={{ once: true }}
                  className="divisor"
                />
              </div>
            </motion.div>
          )}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            transition={{
              duration: 0.5,
              type: "spring",
              bounce: 0.3,
              delay: 0.2,
            }}
            viewport={{ once: true, amount: 0.2 }}
            className="faqWrapper-assessment"
          >
            <h4 className="faq-assessment">FAQ</h4>
            <h2>{t("pages.Diagnosticos.PreviaDiagnostico.aindaTemDuvidas")}</h2>
            <small>
              {t(
                "pages.Diagnosticos.PreviaDiagnostico.aindaTemDuvidasDescricao",
              )}
            </small>
            <div className="card-video-assessment">
              <div className="image-video-assessment-wrapper">
                <img
                  alt="video assessment"
                  src={ImageVideoAssessment}
                  className="image-video-assessment"
                />
                <motion.img
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{
                    scale: 1.05,
                  }}
                  transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
                  alt="play button assessment"
                  src={ImageVidePlayButtonAssessment}
                  className="image-video-play-button-assessment"
                  onClick={() => setVideoPopUpVisible(true)}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div className="content-video-assessment">
                <span className="text">
                  {t(
                    "pages.Diagnosticos.PreviaDiagnostico.entendaComoInterpretarDiagnostico",
                  )}
                </span>
              </div>
            </div>
            <div className="accordion-wrapper">
              <Accordion items={getAccordionItems(t)} />
            </div>
            <div className="assessment-talk-to-us">
              {t("pages.Diagnosticos.faleConosco")}{" "}
              <a href="mailto:support@strm.com.br">
                {" "}
                {t("pages.Diagnosticos.talkToUs")}{" "}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
      <PopUpGlobal
        visible={videoPopUpVisible}
        onClose={() => setVideoPopUpVisible(false)}
      >
        <YouTubePlayer videoId="ukLu7BPLJ6s" />
      </PopUpGlobal>
    </AdvanceWrapper>
  );
};

export default Diagnosticos;
